import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fmtErr } from "@/lib/api";
import { LegalStrip } from "@/components/AppShell";
import { Button, TextField, Logo, VersionChip, useNow, timeET, timeSYD, dayLabel } from "@/sentinel";

const APP_VERSION = process.env.REACT_APP_VERSION || null;

/** A sign-in failure in plain words: wrong password, rate limit (429), disabled account (403), or no connection. */
export function loginErrorMessage(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const text = detail != null ? fmtErr(typeof detail === "object" && detail.message ? detail.message : detail) : "";
  if (!e?.response) return "Can't reach the Sentinel server. Check your connection and try again.";
  if (status === 429) {
    const retry = Number(e.response.headers?.["retry-after"]);
    const wait = Number.isFinite(retry) && retry > 0
      ? retry >= 90 ? `about ${Math.ceil(retry / 60)} minutes` : `${Math.ceil(retry)} seconds`
      : "a few minutes";
    return `Too many sign-in attempts. Wait ${wait} before trying again.`;
  }
  if (status === 403) return text || "This account is disabled. Ask an owner to re-enable it.";
  if (status === 401) return "Email or password is incorrect.";
  return text || e.message || "Sign-in failed.";
}

/** Footer clock: ET first, Sydney second, always labelled. */
export function LoginFooter() {
  const now = useNow(1000);
  const et = timeET(now, { seconds: false });
  const syd = timeSYD(now, { seconds: false });
  return (
    <div className="sn-row sn-small sn-faint" style={{ justifyContent: "space-between" }}>
      <span className="sn-row" style={{ gap: "var(--space-3)", flexWrap: "nowrap" }} aria-label={`Time ${et} Eastern, ${syd} Sydney ${dayLabel(now)}`}>
        <span className="sn-clock"><span className="sn-clock-tz">ET</span><span className="sn-clock-t">{et}</span></span>
        <span className="sn-clock"><span className="sn-clock-tz">SYD</span><span className="sn-clock-t">{syd}</span></span>
      </span>
      <VersionChip version={APP_VERSION} />
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/tonight", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setErr("Enter your email and password.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/tonight", { replace: true });
    } catch (e) {
      setErr(loginErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const errNode = err ? <span data-testid="login-error" role="alert">{err}</span> : null;

  return (
    <div className="sn-app sn-root" style={{ minHeight: "100vh" }}>
      <LegalStrip />
      <div className="sn-login" style={{ flex: 1 }}>
        <div className="sn-login-card sn-stack" style={{ gap: "var(--space-6)" }}>
          <div className="sn-stack" style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
            <Logo size={28} />
            <div>
              <h1 className="text-title" style={{ margin: 0 }}>Sign in to the audit desk</h1>
              <p className="sn-muted" style={{ margin: "var(--space-1) 0 0" }}>
                Decision support for SPX / SPXW 0DTE sessions. Humans confirm; Sentinel never places orders.
              </p>
            </div>
          </div>

          <div className="sn-panel" style={{ padding: "var(--space-6)" }}>
            <form onSubmit={submit} className="sn-stack" style={{ gap: "var(--space-4)" }} data-testid="login-form" noValidate>
              <TextField id="login-email" name="email" label="Email" type="email" inputMode="email" icon="User" autoComplete="username" autoCapitalize="none" spellCheck={false}
                required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
              <TextField id="login-password" name="password" label="Password" type="password" icon="KeyRound" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                error={errNode || undefined} data-testid="login-password" />
              <Button type="submit" variant="primary" size="lg" block loading={busy} data-testid="login-submit">
                {busy ? "Signing in…" : "Sign in"}
              </Button>

              <span className="sn-help">
                Accounts are created by an owner. Forgot your password? Ask an owner to reset it.
              </span>
            </form>
          </div>

          <LoginFooter />
        </div>
      </div>
    </div>
  );
}
